// pages/monitor/panels/peak/PeakReadWrite.js —— 削峰填谷 · 设备读写（依据《云平台单站控制（削峰填谷）需求文档》p50，截图 p050_i091）
// 页面结构：当前设备下拉 + 查询按钮 + 最后一次查询时间；两条黄色安全说明；
// 点位表（参数名称 / 当前值 / 写入值 / 操作），每行「编辑」后填写写入值并「写入」，稍候点击「查询」确认写入成功。
import React, { useState } from 'react';
import { Table, Select, InputNumber, Button, message } from 'antd';
import { getWritePoints, getDevices } from '../../../../config/stationData';
import './peak.css';

const nowStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

// 取值范围字符串（如 "0~100"、"1,2,3"）解析为数字边界；解析失败返回 undefined 不限范围
const rangeMin = (range) => {
  const m = /^(-?[\d.]+)/.exec(String(range || ''));
  return m ? Number(m[1]) : undefined;
};
const rangeMax = (range) => {
  const m = /(-?[\d.]+)$/.exec(String(range || ''));
  return m ? Number(m[1]) : undefined;
};

const PeakReadWrite = ({ station }) => {
  const dev = getDevices(station.id);
  const deviceList = [];
  dev.pcs.forEach((p) => deviceList.push({ id: p.id, name: p.name, group: 'PCS' }));
  dev.racks.forEach((r) => deviceList.push({ id: r.id, name: r.name, group: 'BMS' }));
  dev.hvac.forEach((a) => deviceList.push({ id: a.id, name: a.name, group: '空调' }));
  deviceList.push({ id: dev.liquid.id, name: dev.liquid.name, group: '液冷机组' });
  if (station.has.pv) dev.pv.forEach((p) => deviceList.push({ id: p.id, name: p.name, group: '光伏逆变器' }));

  const [current, setCurrent] = useState(deviceList[0].id);
  const [lastQueryTime, setLastQueryTime] = useState('2025-08-11 07:36:01');
  const [editing, setEditing] = useState({});   // 行 key -> 是否处于编辑（写入值输入）状态
  const [pending, setPending] = useState({});   // 行 key -> 待写入值
  const [override, setOverride] = useState({}); // `${设备id}-${行号}` -> 已写入的当前值（演示）

  const cur = deviceList.find((d) => d.id === current) || deviceList[0];
  const points = getWritePoints(cur.group).map((p, i) => ({
    ...p,
    key: i,
    current: override[`${cur.id}-${i}`] !== undefined ? override[`${cur.id}-${i}`] : p.current,
  }));

  const switchDevice = (v) => { setCurrent(v); setPending({}); setEditing({}); };

  const doQuery = () => {
    setLastQueryTime(nowStr());
    setPending({});
    setEditing({});
    message.success('已重新读取点位当前值，可对照确认此前写入是否生效（演示）');
  };

  const writeRow = (r, i) => {
    const v = pending[r.key];
    if (v === undefined || v === null || v === '') { message.warning('请先填写写入值'); return; }
    message.success(`已向 ${cur.name} 写入「${r.name} = ${v}」（演示，稍候可查询确认）`);
    setOverride((o) => ({ ...o, [`${cur.id}-${i}`]: v }));
    setPending((p) => { const rest = { ...p }; delete rest[r.key]; return rest; });
    setEditing((e) => ({ ...e, [r.key]: false }));
  };

  return (
    <div>
      <div className="peak-toolbar">
        <span className="peak-toolbar-label">当前设备</span>
        <Select size="small" value={current} onChange={switchDevice} style={{ width: 220 }}
          options={deviceList.map((d) => ({ value: d.id, label: d.name }))} />
        <Button size="small" type="primary" onClick={doQuery}>查询</Button>
      </div>
      <div className="pk-rw-time">最后一次查询时间: {lastQueryTime}</div>

      <div className="blk-note" style={{ marginBottom: 16 }}>
        <div className="blk-note-text" style={{ color: '#ffd666' }}>如需对设备点参数进行写入，请联系技术人员进行配置，之后可在此界面操作</div>
        <div className="blk-note-text" style={{ color: '#ffd666', marginTop: 4 }}>为预防安全事故，策略运行时，请勿对PCS、BMS中的特殊点位进行写入操作</div>
        <div className="blk-note-tag">安全</div>
      </div>

      <Table
        size="small" rowKey="key" pagination={false} dataSource={points}
        columns={[
          { title: '参数名称', dataIndex: 'name' },
          { title: '当前值', dataIndex: 'current', width: 160, render: (v) => <span style={{ color: '#7df3d1' }}>{v}</span> },
          {
            title: '写入值', width: 190,
            render: (_, r) => (editing[r.key]
              ? <InputNumber size="small" style={{ width: 160 }} value={pending[r.key]}
                  min={rangeMin(r.range)} max={rangeMax(r.range)}
                  onChange={(v) => setPending({ ...pending, [r.key]: v })} />
              : <span className="pk-rw-placeholder">请输入</span>),
          },
          {
            title: '操作', width: 90,
            render: (_, r, i) => (editing[r.key]
              ? <Button size="small" type="primary" onClick={() => writeRow(r, i)}>写入</Button>
              : <Button size="small" onClick={() => setEditing({ ...editing, [r.key]: true })}>编辑</Button>),
          },
        ]}
      />

      <div className="peak-note">写入后稍候点击「查询」重新读取点位当前值以确认写入成功；写入操作跳过正常控制程序直接下发点位，请谨慎操作。</div>
    </div>
  );
};

export default PeakReadWrite;
